import { parseJsonObject } from "@utils/apiFailure";

export interface ShoppingCartItem {
    id: string;
    productId: string;
    name: string;
    price: number;
    // GET /shoppingcarts sends the offering's vendor per line (Bug #306): the
    // vendor id/name back the "Shop {vendor}" rail; vendorSkuId is the priced
    // offering. `variant` is SINGULAR and nullable on the wire.
    vendorId: number;
    vendorName: string;
    vendorSkuId: string;
    isActive: boolean;
    quantity: number;
    // True when the line cannot ship to the resolved destination (#228 / E4-S12). ALWAYS
    // present on the wire — the handler sets it after projection and writes false when there
    // is no destination — so it is non-optional here: an absent field would be indistinguishable
    // from "not restricted", and the cart's job is to warn before checkout refuses the order.
    isRestricted: boolean;
    variant?: ShoppingCartItemVariant;
}

export interface ShoppingCartItemVariant {
    variantId: string;
    name: string;
    // The variant SKU's full price — never a surcharge to add to the line's price (#432).
    price: number;
    isActive: boolean;
}

export interface ShoppingCartResponse {
    id: string;
    userId: string;
    items: ShoppingCartItem[];
}

// The API's per-line quantity limit (DomainConstraint.ShoppingCartItem.Quantity.MaxValue):
// the add and quantity-update validators refuse more, so no stepper offers more.
export const MAX_LINE_QUANTITY = 9999;

// The machine-readable code of the stock refusals (#443 / C-07): a cart write's 400
// carries it with `available` (and `addable` on add/switch), checkout's 412 with `items`
// (@models/order).
export const INSUFFICIENT_STOCK = "insufficient_stock";

// The other coded refusals of a cart write (add, quantity update, vendor switch). Their
// message is customer-safe and shown as it is.
//   product_unavailable  — the product, the option or its sku is gone: the line can only go.
//   offering_unavailable — the vendor no longer offers it: another vendor may.
//   line_quantity_limit  — the line would hold more than MAX_LINE_QUANTITY (PR 339): an add
//                          or a merge on top of what the line holds, or a raised quantity.
const PRODUCT_UNAVAILABLE = "product_unavailable";
const OFFERING_UNAVAILABLE = "offering_unavailable";
const LINE_QUANTITY_LIMIT = "line_quantity_limit";

const MESSAGE_REFUSAL_KINDS = {
    [PRODUCT_UNAVAILABLE]: "product",
    [OFFERING_UNAVAILABLE]: "offering",
    [LINE_QUANTITY_LIMIT]: "limit",
} as const;

/**
 * A cart write's coded 400, read by its code:
 * - `stock`: `available` is the vendor's whole stock; `addable` (add and switch only) is how
 *   many more this cart can take on top of what it holds.
 * - `product` / `offering` / `limit`: the API's customer-safe `message`.
 */
export type CartWriteRefusal =
    | { kind: "stock"; available: number; addable?: number }
    | { kind: "product" | "offering" | "limit"; message: string };

const isCount = (value: unknown): value is number =>
    typeof value === "number" && Number.isInteger(value) && value >= 0;

/**
 * The cart write's coded refusal, or undefined for anything else — a validation failure,
 * an unknown code, or a code without the fields it promises. Takes the raw body
 * (ApiError.detail).
 */
export const cartWriteRefusal = (
    body: string | undefined,
): CartWriteRefusal | undefined => {
    const parsed = parseJsonObject(body);

    switch (parsed?.error) {
        case INSUFFICIENT_STOCK: {
            const { available, addable } = parsed;

            if (!isCount(available)) {
                return undefined;
            }

            if (addable === undefined) {
                return { kind: "stock", available };
            }

            return isCount(addable)
                ? { kind: "stock", available, addable }
                : undefined;
        }

        case PRODUCT_UNAVAILABLE:
        case OFFERING_UNAVAILABLE:
        case LINE_QUANTITY_LIMIT: {
            const { message } = parsed;

            return typeof message === "string" && message.trim()
                ? {
                    kind: MESSAGE_REFUSAL_KINDS[parsed.error],
                    message: message.trim(),
                }
                : undefined;
        }

        default:
            return undefined;
    }
};

// Mirrors the API's CreateShoppingCartCommandBody (Bug #302): the cart is
// line-per-offering — `vendorSkuId` is hard-required and `variantId` is a
// single optional id. The previous `productVariants: string[]` field never
// existed on the API.
export interface AddToCartRequest {
    productId: string;
    variantId?: string;
    vendorSkuId: string;
    quantity: number;
}
